// Warm, tailored end-of-intervention messaging.
// Each entry exposes:
//   headline     — the big congratulations line shown at the top of the summary
//   opener       — one-sentence warm opener acknowledging what they did
//   trajectory   — short remark pattern based on how their distress moved
//                  (takes delta in 0-10 distress; positive = improvement)
//   closer       — warm parting line encouraging continuation

const buildTrajectoryLine = (delta, opts) => {
  const { strong, moderate, flat, up } = opts;
  if (delta >= 4) return strong;
  if (delta >= 2) return moderate;
  if (delta >= 0) return flat;
  return up;
};

export const INTERVENTION_SYNOPSIS = {
  quiet_the_worry: {
    headline: "You quieted the worry.",
    opener: "Sitting with a worry long enough to soften it takes real courage — and you just did exactly that.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "Your mind visibly unclenched across the session — a meaningful drop in the grip of the worry.",
        moderate:  "The worry lost some of its edge as you worked through the tones.",
        flat:      "The worry held its shape today, but you gave it a full, honest look — that's where the unraveling starts.",
        up:        "The worry got louder for a moment, which often happens as we face it — be gentle with yourself.",
      }),
    closer: "Each time you come back, the worry has a little less to say. Rest well.",
  },
  overcome_overwhelm: {
    headline: "You rode the wave.",
    opener: "Overwhelm can feel all-consuming, and you chose to steady yourself through it instead of fighting it.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "The flood eased — what felt like 'too much' is meaningfully smaller than it was.",
        moderate:  "The surge settled some. Your system moved from crashing wave to rolling tide.",
        flat:      "The intensity hasn't fully lifted, but noticing it is the first act of calming it.",
        up:        "Overwhelm sometimes crests before it breaks — stay with yourself through it.",
      }),
    closer: "You can return whenever the waves pick up again. You know the way now.",
  },
  dial_down_tension: {
    headline: "Your body softened.",
    opener: "You listened to your body instead of pushing through it — that's the whole shift right there.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "A real release — your shoulders, your jaw, the places that were bracing — they unwound.",
        moderate:  "The clench loosened. The body is remembering what ease feels like.",
        flat:      "The tension is holding, but noticing where it lives is itself a form of care.",
        up:        "Sometimes tension surfaces before it dissolves. Be kind to the body that's been holding so much.",
      }),
    closer: "Let your body rest tonight. It's been working hard, and you finally gave it some room.",
  },
  healing_old_stories: {
    headline: "You faced the old story with grace.",
    opener: "Turning toward a memory rather than away from it takes a kind of strength most people never find.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "The old story has visibly less weight than when you started. That matters.",
        moderate:  "The memory softened. It's starting to feel less like a live wire.",
        flat:      "The story is still raw, and that's okay — you don't have to heal it in one sitting.",
        up:        "Old stories often get louder before they settle. You've opened the door — the rest will follow.",
      }),
    closer: "What happened to you doesn't get to define how today feels. Come back whenever you're ready.",
  },
  beat_stage_fright: {
    headline: "You stood down the stage fright.",
    opener: "You looked the fear square in the face — and kept breathing. That is a rehearsal in itself.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "Your nerves eased a noticeable amount — your body now knows this feeling is survivable.",
        moderate:  "The butterflies are calmer. The stage is starting to feel a little less tall.",
        flat:      "The right amount of performance jitter may still be present, but you stayed with it, and that re-writes the pattern effectively.",
        up:        "A spike of nerves is common when we prepare to be seen — it doesn't mean you aren't ready.",
      }),
    closer: "When you step in front of others next, your body will remember this calm. Go shine.",
  },
  fear_of_sleeplessness: {
    headline: "You quieted the mind for rest.",
    opener: "The fear of not sleeping is often louder than the sleeplessness itself — and you turned the volume down.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "A real drop in the sleep worry. Your body should find it much easier to slip into slumber tonight.",
        moderate:  "The dread softened. Your nervous system has a clearer path to rest now.",
        flat:      "The fear is still there, but you gave it something other than a 3am argument.",
        up:        "Sleep fears sometimes rise when we face them — trust that the work is still settling underneath.",
      }),
    closer: "Dim the lights, soften your breath, and let sleep come find you.",
  },
  shake_off_sadness: {
    headline: "You let the blues move through you.",
    opener: "You didn't have to fake it or fix it — you just let the feeling be seen. That is how it begins to shift.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "The heaviness lifted noticeably. A little more light is getting through.",
        moderate:  "The weight is lighter. Your shoulders are a bit freer to carry the day.",
        flat:      "The blues are still here today, and that's allowed. You're not broken for feeling this.",
        up:        "Sadness sometimes deepens before it eases — your willingness to meet it is the whole medicine.",
      }),
    closer: "Be tender with yourself today. Small joys count. Come back any time you need the quiet.",
  },
  boost_self_worth: {
    headline: "You reclaimed a little more of your worth.",
    opener: "You spent time telling yourself a truer story — one where your worth isn't up for debate.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "A real shift — the old inner critic is quieter and your kinder voice is clearer.",
        moderate:  "The self-doubt has less pull. Something steadier is settling in.",
        flat:      "The inner critic is stubborn today, and that's worth noticing gently — not fighting.",
        up:        "Doubt often shouts loudest right before it gives way. Keep showing up for yourself.",
      }),
    closer: "You are worthy of the care you just gave yourself. Carry that with you.",
  },
  hold_boundaries: {
    headline: "You found your steady 'no.'",
    opener: "Holding a boundary starts in the body — and you just rehearsed it in the quietest, safest way.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "A big drop in the internal tug-of-war. Your line feels clearer and more your own.",
        moderate:  "The pull to shrink or over-explain is quieter now.",
        flat:      "The inner push-pull is still present. It will soften as the line gets familiar to your body.",
        up:        "Boundaries stir up guilt before they bring peace — that surge is part of the growth.",
      }),
    closer: "Your 'no' protects your 'yes'. Walk into the next conversation knowing this.",
  },
  relief_from_grief: {
    headline: "You held your grief with tenderness.",
    opener: "Grief doesn't want to be fixed — it wants to be witnessed. You just gave it that.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "The sharpness of the ache eased. The love stays — the edge softens.",
        moderate:  "The weight is a little lighter. Grief and breath are sharing the same room now.",
        flat:      "The ache is still raw today, and that's a measure of how much you loved.",
        up:        "Sometimes grief swells when we finally pause to feel it. You are not going backwards.",
      }),
    closer: "There is no finish line here — only permission to keep going at your own pace.",
  },
  calm_your_anger: {
    headline: "You brought the heat down.",
    opener: "You met your anger with presence instead of fuel — that's the move almost no one makes.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "The fire has real space around it now. Your system found its off-ramp.",
        moderate:  "The burn dialed down. Clearer thinking is coming back online.",
        flat:      "The heat is still up. Staying with it without acting on it is the whole practice.",
        up:        "Anger sometimes flares under attention before it calms — the fact you stayed with it matters.",
      }),
    closer: "Your anger has something to tell you — and it says it more clearly once the heat settles. Rest.",
  },
  enhance_performance: {
    headline: "You prepared from the inside out.",
    opener: "The mental game is half the game — and you just sharpened it with quiet, deliberate focus.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "Your pre-performance nerves eased significantly. You're primed, not pressured.",
        moderate:  "The jitters are lower, the focus is cleaner. Your body knows its job.",
        flat:      "A little nerve is fuel, not a flaw. You brought it in, now let it move you.",
        up:        "Pressure sometimes rises as the moment gets closer. Your body is waking up to the task.",
      }),
    closer: "When it's showtime, your body will remember this steadiness. Trust your training.",
  },
  new_habit_new_you: {
    headline: "You took one more step into the new, healthier you.",
    opener: "New habits aren't built in a day — they're built in quiet moments like this one, when the barriers get smaller and the new path gets clearer.",
    trajectory: (delta) =>
      buildTrajectoryLine(delta, {
        strong:    "The barrier dropped noticeably. The new healthy habit is taking root.",
        moderate:  "The barrier is quieter. The new habit is finding its feet.",
        flat:      "Building is stubborn work — showing up anyway is how the new habit grows.",
        up:        "Barriers often flare when a new habit is close. Keep going — this is normal and temporary.",
      }),
    closer: "Come back tomorrow. Tomorrow's you will thank today's you for starting.",
  },
};

// Fallback synopsis for any intervention key we haven't spelled out.
const FALLBACK = {
  headline: "You did something meaningful today.",
  opener: "You gave yourself quiet time to work through something difficult — that alone is worth honoring.",
  trajectory: (delta) =>
    buildTrajectoryLine(delta, {
      strong:    "Your distress dropped noticeably over the session — a real signal that the work is landing.",
      moderate:  "Your distress eased across the session — progress you can feel.",
      flat:      "The feeling is still present, and that's okay. Simply staying with it is the shift.",
      up:        "Sometimes things intensify before they settle. Be gentle with yourself.",
    }),
  closer: "Come back whenever you need a quiet moment. The door is always open.",
};

export const getCompletionSynopsis = (interventionType, readings) => {
  const template = INTERVENTION_SYNOPSIS[interventionType] || FALLBACK;
  const valid = (readings || []).filter((r) => typeof r.distress === "number");

  if (valid.length < 2) {
    return {
      headline: template.headline,
      opener: template.opener,
      trajectory: "You completed the full flow today. Next time, the readings along the way will show your trajectory.",
      closer: template.closer,
      delta: null,
      start: valid[0]?.distress ?? null,
      end: valid[valid.length - 1]?.distress ?? null,
    };
  }

  const start = valid[0].distress;
  const end = valid[valid.length - 1].distress;
  const delta = start - end; // positive = improvement

  return {
    headline: template.headline,
    opener: template.opener,
    trajectory: template.trajectory(delta),
    closer: template.closer,
    delta,
    start,
    end,
  };
};
